/**
 * Iter XX.267 — Gully Pro Pricing Anchor + Waitlist.
 *
 * ProTierCard is a self-contained, non-purchasable Pro tier card that
 * anchors the Free / Plus / Pro pricing architecture. Pro is Coming
 * Soon at $99/month. The card:
 *   · Fires `pro_plan_viewed` once when it enters the DOM.
 *   · Opens <ProWaitlistSheet /> on CTA press.
 *   · Never modifies entitlements, subscriptions or billing state.
 *
 * Design system: matches XX.266b (dark neutral + restrained emerald +
 * clear spacing hierarchy). No gradients or glows.
 */
import React, { useEffect, useRef, useState } from "react";
import {
  Sparkles, ChevronRight, Mic, Video, LineChart, ClipboardCheck, Activity, Zap,
} from "lucide-react";
import { trackProEvent, PRO_META } from "../lib/proWaitlist";
import ProWaitlistSheet from "./ProWaitlistSheet";
// Iter XX.267b — subtle light haptic on the waitlist CTA.
import { hapticLight } from "../lib/haptics";

// Iter XX.275 — outcome-first Pro benefit rows. Emoji icons swapped
// for lucide-react per app UI guidelines. Copy leads with the cricket
// development outcome, not the technical capability.
const PRO_BENEFITS = [
  { Icon: Mic,             label: "Practice with Gully in real time (voice)" },
  { Icon: Video,           label: "Deeper video analysis and side-by-side comparison" },
  { Icon: LineChart,       label: "Season-long development tracking" },
  { Icon: ClipboardCheck,  label: "Weekly development review" },
  { Icon: Activity,        label: "Compare sessions and see whether something actually changed over time" },
  { Icon: Zap,             label: "Priority processing when things are busy" },
];

export default function ProTierCard({
  source = "upgrade_page",
  sessionId = null,
  className = "",
}) {
  const viewedRef = useRef(false);
  const [sheetOpen, setSheetOpen] = useState(false);

  useEffect(() => {
    if (viewedRef.current) return;
    viewedRef.current = true;
    trackProEvent("pro_plan_viewed", source, sessionId);
  }, [source, sessionId]);

  const openSheet = () => {
    // Iter XX.267b — light haptic on the Join Pro Waitlist CTA.
    hapticLight();
    trackProEvent("pro_waitlist_clicked", source, sessionId);
    setSheetOpen(true);
  };

  return (
    <>
      <div
        data-testid="pro-tier-card"
        data-source={source}
        className={`relative rounded-2xl border border-neutral-800 bg-neutral-950/70 p-6 sm:p-7 ${className}`}
      >
        <div className="flex items-start justify-between gap-4 mb-4">
          <div className="min-w-0">
            <p
              data-testid="pro-tier-eyebrow"
              className="text-[10px] uppercase tracking-[0.14em] text-neutral-500 mb-2"
            >
              Gully Pro · Coming Soon
            </p>
            <h3
              data-testid="pro-tier-headline"
              className="font-display text-2xl sm:text-3xl font-light tracking-tight text-neutral-50 leading-tight"
            >
              For serious cricketers.
            </h3>
            <p className="text-sm text-neutral-400 mt-2 leading-relaxed">
              A cricket development system: live practice with Gully,
              deeper video analysis, and season-long review — all
              connected.
            </p>
          </div>
          <div className="flex-none inline-flex items-center justify-center w-10 h-10 rounded-xl bg-neutral-900 border border-neutral-800 text-neutral-300">
            <Sparkles size={18} strokeWidth={1.5} />
          </div>
        </div>

        <div className="flex items-baseline gap-2 mb-4" data-testid="pro-tier-price">
          <span className="font-display text-3xl text-neutral-100">
            {PRO_META.priceLabel}
          </span>
          <span className="text-neutral-500 text-sm">/ {PRO_META.period}</span>
          <span
            className="ml-auto text-[10px] uppercase tracking-widest text-neutral-500 border border-neutral-800 rounded-full px-2 py-0.5"
            data-testid="pro-tier-coming-soon"
          >
            Coming Soon
          </span>
        </div>

        <ul
          data-testid="pro-tier-benefits"
          className="space-y-2.5 text-sm text-neutral-300 leading-relaxed mb-4"
        >
          {PRO_BENEFITS.map(({ Icon, label }) => (
            <li key={label} className="flex items-start gap-2.5">
              <Icon
                size={16}
                strokeWidth={1.6}
                aria-hidden
                className="flex-none mt-0.5 text-neutral-400"
              />
              <span>{label}</span>
            </li>
          ))}
        </ul>

        {/* Iter XX.275 — core distinction line. Positions Pro as an
            ongoing development system, not a higher subscription tier. */}
        <p
          data-testid="pro-tier-core-distinction"
          className="text-[11px] uppercase tracking-[0.16em] text-neutral-500 mb-6 leading-relaxed"
        >
          Observe. Measure. Remember. Coach over time.
        </p>

        <button
          type="button"
          onClick={openSheet}
          data-testid="pro-tier-waitlist-btn"
          className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-xl border border-neutral-700 text-neutral-100 text-sm font-medium hover:bg-neutral-900 transition-colors"
        >
          Join Pro Waitlist
          <ChevronRight size={16} strokeWidth={1.5} />
        </button>
        <p className="text-[11px] text-neutral-500 mt-2 text-center">
          No charge · we&apos;ll let you know when Pro is ready.
        </p>
      </div>

      {sheetOpen && (
        <ProWaitlistSheet
          source={source}
          sessionId={sessionId}
          onClose={() => setSheetOpen(false)}
        />
      )}
    </>
  );
}
